"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.filterAttributeMap = exports.buildAttributeMap = exports.asAttributeMap = exports.amplifyReporter = void 0;
const auth_1 = require("@aws-amplify/auth");
const analytics_1 = require("@aws-amplify/analytics");
function amplifyReporter(info, config) {
    var _a;
    auth_1.Auth.configure({
        region: config.region,
        identityPoolId: config.identityPoolId,
        userPoolId: config.userPoolId,
        userPoolWebClientId: config.userPoolWebClientId,
    });
    const allMetadata = asAttributeMap({
        appName: info.service,
        service: info.service,
        domain: window.location.host,
        environment: info.service,
        version: info.version,
    });
    analytics_1.Analytics.configure({
        region: config.region,
        appId: config.analyticsAppId,
        autoSessionRecord: config.autoTrackSessions,
        endpoint: {
            attributes: allMetadata,
        },
        ...config.buffering,
    });
    if (config.autoTrackPageViews) {
        analytics_1.Analytics.autoTrack('pageView', {
            enable: true,
            eventName: 'pageView',
            type: 'SPA',
            provider: 'AWSPinpoint',
        });
    }
    if (config.autoTrackEvents) {
        analytics_1.Analytics.autoTrack('event', {
            enable: true,
            selectorPrefix: (_a = config.selectorPrefix) !== null && _a !== void 0 ? _a : 'data-analytics-',
        });
    }
    const reporter = {
        trackEvent: function (event) {
            analytics_1.Analytics.record({
                name: event.message,
                attributes: asAttributeMap({
                    ...event.metadata,
                    ...event.tags,
                }, false, config.ignoreMetadataPatterns),
                metrics: event.metrics,
            });
        },
        addBreadcrumb: function (breadcrumb) {
            var _a;
            if (breadcrumb.category && ((_a = config.ignoreBreadcrumbCategories) === null || _a === void 0 ? void 0 : _a.includes(breadcrumb.category))) {
                return;
            }
            reporter.trackEvent({
                message: breadcrumb.message,
                metadata: {
                    category: breadcrumb.category,
                    ...breadcrumb.metadata,
                },
            });
        },
        addMetadata: function (metadata) {
            Object.assign(allMetadata, asAttributeMap(metadata, true, config.ignoreMetadataPatterns));
            analytics_1.Analytics.updateEndpoint({
                attributes: allMetadata,
            }).catch(() => {
                // Swallow; see: https://crimsonhq.slack.com/archives/G4UN6Q4KF/p1648599302847539
            });
        },
        setUser: function (user) {
            var _a, _b;
            analytics_1.Analytics.updateEndpoint({
                userId: (_a = user === null || user === void 0 ? void 0 : user.id) !== null && _a !== void 0 ? _a : '',
                userAttributes: user
                    ? asAttributeMap({
                        id: user.id,
                        email: user.email,
                        name: (_b = user.name) !== null && _b !== void 0 ? _b : user.email,
                        username: user.username,
                    })
                    : {},
            }).catch(() => {
                // Swallow; see: https://crimsonhq.slack.com/archives/G4UN6Q4KF/p1648599302847539
            });
        },
        setRouteName: function (routeName) {
            reporter.addMetadata({ routeName });
        },
        setPageName: function (pageName) {
            reporter.addMetadata({ pageName });
        },
        reportError: function () { },
        recordSession: function () { },
        recordSessionStop: function () { },
    };
    return reporter;
}
exports.amplifyReporter = amplifyReporter;
/**
 * Pinpoint has strict attribute name and value length limits
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function asAttributeMap(values, groupValues = true, ignorePatterns = []) {
    const attributeMap = buildAttributeMap(values, undefined, groupValues);
    const filteredAttributeMap = filterAttributeMap(attributeMap, ignorePatterns);
    const checkedEntries = Object.entries(filteredAttributeMap).map(([key, value]) => {
        var _a, _b;
        const truncatedKey = key.length > 50 ? `___${key.slice(-47)}` : key;
        const truncatedValue = Array.isArray(value)
            ? (_a = value === null || value === void 0 ? void 0 : value.map((val) => val.slice(0, 100))) !== null && _a !== void 0 ? _a : null
            : (_b = value === null || value === void 0 ? void 0 : value.slice(0, 100)) !== null && _b !== void 0 ? _b : null;
        return [truncatedKey, truncatedValue];
    });
    return Object.fromEntries(checkedEntries);
}
exports.asAttributeMap = asAttributeMap;
/**
 * Pinpoint expects `endpoint.attributes` and `endpoint.userAttributes` to have
 * values which are string arrays. This function takes in an object and ensures
 * all of its values are of type `string[]` to appease Pinpoint.
 */
function buildAttributeMap(
// eslint-disable-next-line @typescript-eslint/no-explicit-any
values, parentKey = undefined, groupValues = true) {
    const valuesWithStringArrays = {};
    Object.entries(values).forEach(([key, value]) => {
        const combinedKey = parentKey ? [parentKey, key].join('.') : key;
        if (!value) {
            valuesWithStringArrays[combinedKey] = null;
        }
        else if (groupValues && Array.isArray(value)) {
            valuesWithStringArrays[combinedKey] = value.map((element) => typeof element === 'string' ? element : JSON.stringify(element));
        }
        else if (typeof value === 'object') {
            const flattenedAttribute = buildAttributeMap(value, combinedKey, groupValues);
            Object.assign(valuesWithStringArrays, flattenedAttribute);
        }
        else {
            const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
            valuesWithStringArrays[combinedKey] = groupValues ? [stringValue] : stringValue;
        }
    });
    return valuesWithStringArrays;
}
exports.buildAttributeMap = buildAttributeMap;
function filterAttributeMap(attributes, ignorePatterns) {
    const entries = Object.entries(attributes);
    return Object.fromEntries(entries.filter(([key]) => !ignorePatterns.some((pattern) => pattern.test(key))));
}
exports.filterAttributeMap = filterAttributeMap;
//# sourceMappingURL=amplifyReporter.js.map